import { exportToExcel } from '@features/downloads/util/exportToExcel';
import { InitiativeBenchmarking } from '@g17eco/types/initiative';
import { getPeriodName } from '@utils/dataPeriods';
import { DATE, formatDateNonUTC } from '@utils/date';
import { QUESTION, SURVEY } from '@constants/terminology';
import { ExchangeSurveyWithCompany } from './types';
import { getFormattedSurveyDate } from '@utils/survey';
import { standards, frameworks } from '@g17eco/core';

const getPercentData = (numerator: number, denominator: number) => {
  const percent = denominator ? (numerator * 100) / denominator : 0;
  return {
    display: percent ? `${percent.toFixed(1)}%` : '0%',
    percent,
  };
};

export const hasCompletedSurveys = (company: InitiativeBenchmarking) =>
  (company.benchmarkingSurveys ?? []).some((survey) => survey.completedDate);

export const getCompanyName = (survey: ExchangeSurveyWithCompany) => {
  return survey.company.name;
};

export const getEffectiveDate = (survey: ExchangeSurveyWithCompany) => {
  return {
    display: getFormattedSurveyDate(survey),
    sort: `${survey.effectiveDate} ${getPeriodName(survey.period, false)}`,
  };
};

export const getCompletedDate = (survey: ExchangeSurveyWithCompany) => {
  return survey.completedDate ? formatDateNonUTC(survey.completedDate, DATE.MONTH_YEAR_SHORT) : '-';
};

export const getProgress = (survey: ExchangeSurveyWithCompany) => {
  return getPercentData(survey.answered, survey.total);
};

export const getTotalQuestions = (survey: ExchangeSurveyWithCompany) => {
  return survey.total;
};

export const getUnanswered = (survey: ExchangeSurveyWithCompany) => {
  return survey.total - survey.answered;
};

export const getAnswered = (survey: ExchangeSurveyWithCompany) => {
  return survey.answered;
};

export const getPercentPrivate = (survey: ExchangeSurveyWithCompany) => {
  return getPercentData(survey.private, survey.total);
};

export const getPercentNr = (survey: ExchangeSurveyWithCompany) => {
  return getPercentData(survey.nr, survey.total);
};

export const getPercentNa = (survey: ExchangeSurveyWithCompany) => {
  return getPercentData(survey.na, survey.total);
};

export const getModulesDisplay = (survey: ExchangeSurveyWithCompany) => {
  const scopeStandards = survey.scope?.standards ?? [];
  const scopeFrameworks = survey.scope?.frameworks ?? [];

  const standardNames = scopeStandards
    .map((code: string) => standards[code]?.shortName)
    .filter(Boolean);

  const frameworkNames = scopeFrameworks
    .map((code: string) => frameworks[code]?.shortName)
    .filter(Boolean);

  const allModules = [...standardNames, ...frameworkNames];
  return allModules.join(', ') || '-';
};

const rowHeaders = [
  'Company name',
  `${SURVEY.CAPITALIZED_ADJECTIVE} date`,
  'Completed date',
  'Progress',
  QUESTION.CAPITALIZED_PLURAL,
  'Unanswered',
  'Answered',
  '% N/A',
  '% N/R',
  '% Private',
  'Modules',
];

export const downloadBenchmarkingSurveys = (surveys: ExchangeSurveyWithCompany[]) => {
  const rowValues = surveys.map((survey) => {
    return [
      getCompanyName(survey),
      getEffectiveDate(survey).display,
      getCompletedDate(survey),
      getProgress(survey).display,
      getTotalQuestions(survey),
      getUnanswered(survey),
      getAnswered(survey),
      getPercentNa(survey).display,
      getPercentNr(survey).display,
      getPercentPrivate(survey).display,
      getModulesDisplay(survey),
    ];
  });

  exportToExcel({ headers: rowHeaders, values: rowValues, fileName: 'benchmarking-surveys' });
};
